import { motion } from "framer-motion";
import {
  blueStar,
  newOilBanner,
  orientationProg,
  progranStart,
} from "../../../assets";

const events = [
  {
    number: "01",
    title: "New Oil Launch",
    category: "Industry & Innovation",
    image: newOilBanner,
    description:
      "An exciting look into innovation, entrepreneurship and the future of technology.",
  },
  {
    number: "02",
    title: "Orientation Program",
    category: "Community",
    image: orientationProg,
    description:
      "Welcoming a new generation of learners into the Zulfah Academy community.",
  },
  {
    number: "03",
    title: "Program Start",
    category: "Learning",
    image: progranStart,
    description:
      "The beginning of another chapter of practical learning, mentorship and transformation.",
  },
];

const OurEvent = () => {
  return (
    <section className="relative overflow-hidden bg-white py-28 md:py-36">

      {/* =====================================================
          BACKGROUND DECORATION
      ===================================================== */}

      <div className="absolute inset-0 pointer-events-none">

        {/* Teal glow */}
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#59b5cd]/10 blur-[140px]" />

        {/* Gold glow */}
        <div className="absolute bottom-0 -left-40 w-[450px] h-[450px] rounded-full bg-[#ecb32c]/10 blur-[140px]" />

        {/* Soft grid */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              "linear-gradient(#07171c 1px, transparent 1px), linear-gradient(90deg, #07171c 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

      </div>


      <div className="relative z-10 max-w-7xl mx-auto px-6">

        {/* =====================================================
            HEADER
        ===================================================== */}

        <motion.div
          className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >

          <div className="max-w-2xl">

            {/* Eyebrow */}

            <div className="flex items-center gap-3 mb-5">

              <span className="w-8 h-[2px] bg-[#ecb32c]" />

              <span className="text-[#ecb32c] text-xs font-black uppercase tracking-[0.3em]">
                The Zulfah Experience
              </span>

            </div>


            <h2 className="text-4xl md:text-6xl font-black text-[#07171c] leading-[1.05]">

              More than
              <span className="text-[#59b5cd]"> classrooms.</span>

              <br />

              <span className="text-[#07171c]">
                We build
              </span>{" "}

              <span className="bg-gradient-to-r from-[#ecb32c] to-[#d79d13] bg-clip-text text-transparent">
                experiences.
              </span>

            </h2>


            <p className="mt-6 text-gray-500 text-base md:text-lg leading-relaxed">
              From industry conversations and orientation days to program
              launches, our community learns, connects and grows beyond the
              classroom.
            </p>

          </div>


          {/* Event count */}

          <div className="hidden md:flex items-center gap-4">

            <div className="w-14 h-14 rounded-2xl bg-[#07171c] flex items-center justify-center">

              <span className="text-[#ecb32c] font-black text-lg">
                03
              </span>

            </div>

            <div>

              <p className="text-[#07171c] font-bold">
                Recent Moments
              </p>

              <p className="text-gray-400 text-sm">
                Explore our community
              </p>

            </div>

          </div>

        </motion.div>


        {/* =====================================================
            EVENT GRID
        ===================================================== */}

        <div className="grid lg:grid-cols-12 gap-6">

          {/* =================================================
              FEATURED EVENT
          ================================================= */}

          <motion.div
            className="lg:col-span-7 relative min-h-[500px] md:min-h-[600px] rounded-[2rem] overflow-hidden group cursor-pointer"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            whileHover={{ y: -6 }}
          >

            {/* Image */}

            <img
              src={events[0].image}
              alt={events[0].title}
              className="
                absolute
                inset-0
                w-full
                h-full
                object-cover
                transition-transform
                duration-700
                group-hover:scale-105
              "
            />


            {/* Dark overlay */}

            <div className="absolute inset-0 bg-gradient-to-t from-[#07171c] via-[#07171c]/40 to-transparent" />

            {/* Teal overlay */}

            <div className="absolute inset-0 bg-gradient-to-br from-[#59b5cd]/10 via-transparent to-[#ecb32c]/10 opacity-0 group-hover:opacity-100 transition duration-500" />


            {/* Top content */}

            <div className="absolute top-6 left-6 right-6 flex justify-between items-start">

              <div className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10">

                <span className="text-white text-xs font-bold uppercase tracking-wider">
                  Featured Event
                </span>

              </div>


              <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/10 flex items-center justify-center">

                <span className="text-white font-black text-sm">
                  {events[0].number}
                </span>

              </div>

            </div>


            {/* Star */}

            <motion.img
              src={blueStar}
              alt=""
              className="absolute top-24 right-8 w-10 opacity-0 group-hover:opacity-100"
              initial={{ rotate: -20, scale: 0.5 }}
              whileHover={{ rotate: 20, scale: 1.1 }}
              transition={{ duration: 0.4 }}
            />


            {/* Bottom content */}

            <div className="absolute bottom-0 left-0 right-0 p-7 md:p-10">

              <p className="text-[#ecb32c] text-xs font-black uppercase tracking-[0.2em] mb-3">
                {events[0].category}
              </p>

              <h3 className="text-3xl md:text-5xl font-black text-white leading-tight">
                {events[0].title}
              </h3>

              <p className="mt-4 max-w-xl text-white/60 leading-relaxed">
                {events[0].description}
              </p>


              {/* CTA */}

              <div className="mt-7 inline-flex items-center gap-3 text-white font-bold group-hover:text-[#ecb32c] transition-colors">

                <span>
                  Explore Event
                </span>

                <span className="text-xl transition-transform group-hover:translate-x-2">
                  →
                </span>

              </div>

            </div>

          </motion.div>


          {/* =================================================
              SECONDARY EVENTS
          ================================================= */}

          <div className="lg:col-span-5 grid grid-cols-1 gap-6">

            {events.slice(1).map((event, index) => (

              <motion.div
                key={event.number}
                className="
                  relative
                  min-h-[280px]
                  md:min-h-[288px]
                  rounded-[2rem]
                  overflow-hidden
                  group
                  cursor-pointer
                "
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.7,
                  delay: index * 0.15,
                }}
                whileHover={{ y: -6 }}
              >

                {/* Image */}

                <img
                  src={event.image}
                  alt={event.title}
                  className="
                    absolute
                    inset-0
                    w-full
                    h-full
                    object-cover
                    transition-transform
                    duration-700
                    group-hover:scale-110
                  "
                />


                {/* Overlay */}

                <div className="absolute inset-0 bg-gradient-to-t from-[#07171c] via-[#07171c]/45 to-transparent" />


                {/* Gold hover */}

                <div className="absolute inset-0 bg-gradient-to-br from-[#59b5cd]/10 to-[#ecb32c]/10 opacity-0 group-hover:opacity-100 transition duration-500" />


                {/* Number */}

                <div className="absolute top-5 right-5 w-11 h-11 rounded-full bg-black/20 backdrop-blur-md border border-white/10 flex items-center justify-center">

                  <span className="text-white text-xs font-black">
                    {event.number}
                  </span>

                </div>


                {/* Content */}

                <div className="absolute bottom-0 left-0 right-0 p-6">

                  <p className="text-[#ecb32c] text-[10px] font-black uppercase tracking-[0.2em] mb-2">
                    {event.category}
                  </p>

                  <h3 className="text-2xl md:text-3xl font-black text-white">
                    {event.title}
                  </h3>

                  <div className="mt-3 flex items-center gap-2 text-white/60 text-sm group-hover:text-white transition">

                    <span>
                      View event
                    </span>

                    <span className="transition-transform group-hover:translate-x-2">
                      →
                    </span>

                  </div>

                </div>

              </motion.div>

            ))}

          </div>

        </div>


        {/* =====================================================
            BOTTOM CTA
        ===================================================== */}

        <motion.div
          className="mt-8 rounded-[2rem] bg-[#07171c] p-7 md:p-9 relative overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >

          {/* Glow */}

          <div className="absolute -right-20 -top-20 w-60 h-60 rounded-full bg-[#59b5cd]/20 blur-[90px]" />

          <div className="absolute -left-20 -bottom-20 w-60 h-60 rounded-full bg-[#ecb32c]/10 blur-[90px]" />


          <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">

            <div>

              <p className="text-[#59b5cd] text-xs font-black uppercase tracking-[0.2em]">
                Stay connected
              </p>

              <h3 className="mt-2 text-xl md:text-2xl font-black text-white">
                There's always something happening at Zulfah.
              </h3>

            </div>


            <a
              href="/events"
              className="
                inline-flex
                items-center
                justify-center
                gap-3
                px-6
                py-3.5
                rounded-xl
                bg-[#ecb32c]
                text-[#07171c]
                font-bold
                hover:bg-[#f3c54f]
                hover:scale-[1.03]
                transition-all
                whitespace-nowrap
              "
            >
              View All Events
              <span className="text-lg">
                →
              </span>
            </a>

          </div>

        </motion.div>

      </div>

    </section>
  );
};

export default OurEvent;